import { Footer } from "../../components/Footer";
import { Link } from "../../components/Link";
import { TrustSafety } from "../../components/TrustSafety";

const trainingSteps = [
  {
    eyebrow: "01",
    title: "Record your form",
    description:
      "Use your camera to capture staff, spear, or sparring drills from the angle you already train with.",
  },
  {
    eyebrow: "02",
    title: "Read the motion",
    description:
      "Sifu.art highlights timing, alignment, balance, and edge control so the next rep has a clear focus.",
  },
  {
    eyebrow: "03",
    title: "Replay and refine",
    description:
      "Compare attempts, keep the strongest version, and build a repeatable training rhythm.",
  },
];

const outcomes = [
  "Sharper footwork and staff path awareness",
  "Replay loops for solo practice between classes",
  "Beginner-friendly notes without flattening advanced style",
  "A visual record of progress across sessions",
];

const featureChapters = [
  {
    title: "Replay",
    description:
      "Slow down the exact moment a strike drifts, then train the correction until it becomes automatic.",
    image: "/images/eye-focus.webp",
  },
  {
    title: "Recreate",
    description:
      "Use AI-guided form notes to rebuild a movement while keeping your own martial signature intact.",
    image: "/images/webcam.webp",
  },
  {
    title: "Perform",
    description:
      "Bring cleaner mechanics back into live drills, demos, and competition prep.",
    image: "/images/spar.jpg",
  },
];

function DemoButton({ className = "" }: { className?: string }) {
  return (
    <Link
      href="/staffdemo"
      className={`inline-flex min-h-12 items-center justify-center rounded-md border-2 border-black bg-white px-6 font-cinzel text-sm font-bold uppercase tracking-[0.18em] text-[#d83c18] shadow-[7px_7px_0_#000] transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[9px_9px_0_#000] ${className}`}
    >
      Live Demo
    </Link>
  );
}

export default function Page() {
  return (
    <div className="min-h-screen bg-[#f4efe6] text-[#111111]">
      <main id="main-content">
        <section className="relative isolate flex min-h-[92svh] w-full items-end overflow-hidden bg-[#08111d] px-5 pb-14 pt-24 text-white sm:px-8 lg:px-12">
          <div className="absolute inset-0 -z-20 bg-[url(/images/staffmartialart.png)] bg-cover bg-[70%_center] md:bg-center" />
          <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#08111d]/20 via-[#08111d]/52 to-[#08111d]" />

          <div className="mx-auto grid w-full max-w-[1180px] gap-10 lg:grid-cols-[minmax(0,0.95fr)_minmax(320px,0.55fr)] lg:items-end">
            <div className="max-w-[760px]">
              <p className="font-cinzel text-xs font-bold uppercase tracking-[0.34em] text-[#ffcf72]">
                AI motion feedback for staff martial arts
              </p>
              <h1 className="mt-5 max-w-[12ch] font-cinzel text-[52px] font-bold uppercase leading-[0.9] sm:text-[76px] lg:text-[96px]">
                Train staff form with sharper feedback.
              </h1>
              <p className="mt-6 max-w-[650px] font-spectral text-xl leading-8 text-white/[0.88] sm:text-2xl">
                Sifu.art turns camera practice into focused replay, motion
                notes, and repeatable drills for practitioners who want every
                rep to teach them something.
              </p>
              <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
                <DemoButton />
                <a
                  href="#how-it-works"
                  className="inline-flex min-h-12 items-center justify-center rounded-md border border-white/30 px-6 font-cinzel text-sm font-bold uppercase tracking-[0.18em] text-white transition-colors hover:bg-white/10"
                >
                  How It Works
                </a>
              </div>
            </div>

            <div className="max-w-[420px] border-l-4 border-[#ec4e20] bg-black/45 p-6 backdrop-blur-sm">
              <p className="font-cinzel text-sm font-bold uppercase tracking-[0.24em] text-white">
                Perform. Replay. Evolve.
              </p>
              <p className="mt-4 font-spectral text-lg leading-7 text-white/[0.82]">
                Built for solo reps, class homework, demo prep, and anyone who
                needs a clearer view of what their body is actually doing.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-white px-5 py-14 sm:px-8 lg:px-12">
          <div className="mx-auto grid max-w-[1180px] gap-8 md:grid-cols-4">
            {outcomes.map((outcome) => (
              <div
                key={outcome}
                className="border-t-4 border-[#ec4e20] pt-5 font-spectral text-lg leading-7"
              >
                {outcome}
              </div>
            ))}
          </div>
        </section>

        <TrustSafety />

        <section
          id="how-it-works"
          className="bg-[#f4efe6] px-5 py-20 sm:px-8 lg:px-12"
        >
          <div className="mx-auto max-w-[1180px]">
            <div className="max-w-[720px]">
              <p className="font-cinzel text-xs font-bold uppercase tracking-[0.32em] text-[#d83c18]">
                Recommended Training Flow
              </p>
              <h2 className="mt-4 font-cinzel text-4xl font-bold uppercase leading-tight sm:text-5xl">
                Record, review, refine.
              </h2>
              <p className="mt-5 font-spectral text-xl leading-8 text-[#2b2b2b]">
                Move from raw footage to focused corrections with a loop
                designed for steady practice.
              </p>
            </div>

            <div className="mt-12 grid gap-5 md:grid-cols-3">
              {trainingSteps.map((step) => (
                <article
                  key={step.title}
                  className="min-h-[250px] rounded-md border-2 border-black bg-white p-6 shadow-[8px_8px_0_#111111]"
                >
                  <p className="font-cinzel text-sm font-bold tracking-[0.22em] text-[#ec4e20]">
                    {step.eyebrow}
                  </p>
                  <h3 className="mt-8 font-cinzel text-2xl font-bold uppercase">
                    {step.title}
                  </h3>
                  <p className="mt-4 font-spectral text-lg leading-7 text-[#303030]">
                    {step.description}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="grid bg-[#08111d] text-white lg:grid-cols-2">
          <div className="relative min-h-[420px] overflow-hidden">
            <video
              className="absolute inset-0 h-full w-full object-cover"
              src="/videos/pole_10s.webm"
              poster="/images/pole.jpg"
              autoPlay
              loop
              muted
              playsInline
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#08111d]/70 to-transparent" />
          </div>
          <div className="flex items-center px-5 py-16 sm:px-8 lg:px-12">
            <div className="max-w-[560px]">
              <p className="font-cinzel text-xs font-bold uppercase tracking-[0.32em] text-[#ffcf72]">
                Product Preview
              </p>
              <h2 className="mt-4 font-cinzel text-4xl font-bold uppercase leading-tight sm:text-5xl">
                See the feedback before you change the rep.
              </h2>
              <p className="mt-5 font-spectral text-xl leading-8 text-white/[0.86]">
                Watch a rep, spot the drift, and return to practice with one
                clear adjustment.
              </p>
              <DemoButton className="mt-8" />
            </div>
          </div>
        </section>

        <section className="bg-white px-5 py-20 sm:px-8 lg:px-12">
          <div className="mx-auto max-w-[1180px]">
            <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
              <div className="max-w-[680px]">
                <p className="font-cinzel text-xs font-bold uppercase tracking-[0.32em] text-[#d83c18]">
                  Core Sections
                </p>
                <h2 className="mt-4 font-cinzel text-4xl font-bold uppercase leading-tight sm:text-5xl">
                  Replay, recreate, perform.
                </h2>
              </div>
              <p className="max-w-[390px] font-spectral text-lg leading-7 text-[#333333]">
                Replay footage, rebuild cleaner mechanics, and carry sharper
                movement back into live practice.
              </p>
            </div>

            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {featureChapters.map((feature) => (
                <article
                  key={feature.title}
                  className="overflow-hidden rounded-md border-2 border-black bg-[#f4efe6] shadow-[8px_8px_0_#111111]"
                >
                  <div
                    className="aspect-[4/3] bg-cover bg-center"
                    style={{ backgroundImage: `url(${feature.image})` }}
                  />
                  <div className="p-6">
                    <h3 className="font-cinzel text-2xl font-bold uppercase">
                      {feature.title}
                    </h3>
                    <p className="mt-4 font-spectral text-lg leading-7 text-[#303030]">
                      {feature.description}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden bg-[#ec4e20] px-5 py-16 text-white sm:px-8 lg:px-12">
          <div className="absolute inset-y-0 right-0 hidden w-1/2 bg-[url(/images/wallblue.webp)] bg-cover bg-center opacity-30 md:block" />
          <div className="relative mx-auto flex max-w-[1180px] flex-col gap-8 md:flex-row md:items-center md:justify-between">
            <div className="max-w-[680px]">
              <p className="font-cinzel text-xs font-bold uppercase tracking-[0.32em] text-white/[0.78]">
                Start with one drill
              </p>
              <h2 className="mt-4 font-cinzel text-4xl font-bold uppercase leading-tight sm:text-5xl">
                Put your next staff rep on camera.
              </h2>
            </div>
            <DemoButton />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
